import {
  Select as MantineSelect,
  SelectProps as MantineSelectProps,
} from "@mantine/core";
import { forwardRef, useRef, useState } from "react";

import { Icon } from "../../../assets/icons/Icon";
import Spinner from "../../ui/spinner/Spinner";
import Button from "../button/Button";
import styles from "./Select.module.scss";

export interface SelectProps extends Omit<MantineSelectProps, "classNames"> {
  label?: string | JSX.Element;
  error?: string;
  className?: string;
  isSearch?: boolean;
  isLoading?: boolean;
}

const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      label,
      error,
      className,
      searchable,
      data = [],
      isSearch,
      isLoading,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const setRefs = (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === "function") ref(element);
      else if (ref) ref.current = element;
    };

    const isDataEmpty = !data || (Array.isArray(data) && data.length === 0);

    return (
      <div className={`${styles.select} ${className || ""}`}>
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        {label && <label className={styles.selectLabel}>{label}</label>}
        <MantineSelect
          ref={setRefs}
          data={data}
          {...props}
          searchable={searchable !== undefined ? searchable : true}
          withCheckIcon={false}
          onDropdownOpen={() => {
            if (!isDataEmpty) {
              setIsOpen(true);
            }
          }}
          onDropdownClose={() => setIsOpen(false)}
          withScrollArea={false}
          nothingFoundMessage="Not Found"
          styles={{ dropdown: { maxHeight: 200, overflowY: "auto" } }}
          classNames={{
            wrapper: styles.selectWrapper,
            input: `${styles.selectInput} ${isSearch ? styles.isSearchInput : ""}`,
            dropdown: isDataEmpty ? styles.selectListEmpty : styles.selectList,
            option: styles.selectOption,
            error: styles.selectError,
          }}
          leftSection={
            isSearch && (
              <Button variant="nohover" className={styles.selectSearchButton}>
                {isLoading ? (
                  <Spinner size="1rem" />
                ) : (
                  <Icon
                    iconName="SearchIcon"
                    width="1rem"
                    height="1.26rem"
                    // @ts-expect-error TS2339 non-existing className
                    className={styles.button}
                  />
                )}
              </Button>
            )
          }
          rightSection={
            <div
              role="button"
              // @ts-expect-error TS2551 non-existing className
              className={`${styles.selectIcon} ${isOpen ? styles.selectIconUp : ""} ${isDataEmpty ? styles.selectIconNoData : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (inputRef.current) {
                  inputRef.current.click();
                }
              }}
            >
              <Icon
                iconName={isOpen ? "IconUp" : "IconDown"}
                width="1rem"
                height="1rem"
              />
            </div>
          }
        />
        {error && <div className={styles.selectErrorText}>{error}</div>}
      </div>
    );
  },
);

Select.displayName = "Select";
export default Select;
